import type React from 'react'

import {PageDataContextProvider} from '@github-ui/pull-request-page-data-tooling/page-data-context'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {PullRequestErrorState} from './components/PullRequestErrorState'
import {
  condensedResponsiveWrapperClasses,
  fullWidthResponsiveWrapperClasses,
} from './components/StickyPullRequestHeader'
import {SplitPageLayout} from '@primer/react'
import {LivePullRequestHeader} from './components/PullRequestHeader'
import type {PropsWithChildren} from 'react'
import type {HeaderPageData} from './page-data/payloads/header'
import {useMatch} from '@github-ui/react-router'
import {FilesRoutePath} from './routes/route-paths'
import {useRouteHeaderData} from './hooks/use-route-header-data'

/**
 * The App component is used to render content which should be present on _all_ non-DataRouter routes within this app
 */
export function App(props: {children?: React.ReactNode}) {
  return (
    <ErrorBoundary critical fallback={<PullRequestErrorState />}>
      <Layout>{props.children}</Layout>
    </ErrorBoundary>
  )
}

function Layout(props: {children?: React.ReactNode}) {
  const {aliveChannel, pullRequest, bannersData, repository, urls, user} = useRouteHeaderData()

  return (
    <LayoutComponent
      aliveChannel={aliveChannel}
      pullRequest={pullRequest}
      bannersData={bannersData}
      repository={repository}
      urls={urls}
      user={user}
    >
      {props.children}
    </LayoutComponent>
  )
}

export function LayoutComponent({
  aliveChannel,
  pullRequest,
  bannersData,
  repository,
  urls,
  user,
  children,
}: PropsWithChildren<HeaderPageData>) {
  const isFilesRoute = useMatch(`${FilesRoutePath}/*`) !== null

  const responsiveWrapperClasses = isFilesRoute ? fullWidthResponsiveWrapperClasses : condensedResponsiveWrapperClasses

  return (
    <PageDataContextProvider basePageDataUrl={urls.conversation}>
      <div className={`mt-4 ${responsiveWrapperClasses}`}>
        <SplitPageLayout>
          <SplitPageLayout.Header divider="none" padding="none">
            <LivePullRequestHeader
              aliveChannel={aliveChannel}
              bannersData={bannersData}
              isFilesRoute={isFilesRoute}
              repository={repository}
              pullRequest={pullRequest}
              urls={urls}
              user={user}
            />
          </SplitPageLayout.Header>
          {children}
        </SplitPageLayout>
      </div>
    </PageDataContextProvider>
  )
}

try{ App.displayName ||= 'App' } catch {}
try{ Layout.displayName ||= 'Layout' } catch {}
try{ LayoutComponent.displayName ||= 'LayoutComponent' } catch {}